import React, { useState, useRef } from 'react';
import {
  Button,
  ButtonGroup,
  MenuItem,
  Menu,
  Box
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SnackBarMessage } from '../../../components/snackBar/snackBar';

const options = [
  { label: 'Download User Search', value: 'search' },
  { label: 'Download PAYG Report', value: 'payg' }
];

interface DownloadSectionProps {
  onClickDownloadSearchTable: () => void;
  onClickDownloadPaygReport: (team: string) => void;
  teams: any;
  isDisabled: boolean;
}

const DownloadUserReport: React.FC<DownloadSectionProps> = ({
  onClickDownloadSearchTable,
  onClickDownloadPaygReport,
  teams,
  isDisabled
}) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showTeamMenu, setShowTeamMenu] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false
  });

  const anchorRef = useRef<HTMLDivElement>(null);
  const teamButtonRef = useRef<HTMLDivElement | null>(null);

  const handleMainClick = () => {
    const selectedValue = options[selectedIndex].value;
    if (selectedValue === 'search') {
      onClickDownloadSearchTable();
    } else if (selectedValue === 'payg') {
      onClickDownloadPaygReport(selectedTeam?.name || '');
    }
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setSelectedIndex(index);
    setSelectedTeam('');
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prev => !prev);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleTeamMenuOpen = () => {
    setShowTeamMenu(true);
  };

  const handleTeamMenuClose = () => {
    setShowTeamMenu(false);
  };

  const handleTeamChange = (team: any) => {
    setSelectedTeam(team);
    setShowTeamMenu(false);
  };

  const handleSnackBarMessage = () => {
    setMsgConfig({
      type: 'warning',
      message: "At least select one of the filters or Date range",
      snackFlag: true
    });
  };

  return (
    <Box sx={{ maxHeight: '100%', display: 'flex', gap: '10px' }}>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="split download button"
        sx={{ boxShadow: 'none', height: '100%' }}
      >
        <Button
          sx={{ px: "12px" }}
          onClick={() => {
            if (selectedIndex === 0) {
              isDisabled ? handleSnackBarMessage() : handleMainClick();
            } else {
              handleMainClick();
            }
          }}
        >
          {options[selectedIndex].label}
        </Button>
        <Button
          size="small"
          onClick={handleToggle}
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select download option"
          aria-haspopup="menu"
          sx={{ p: "0px !important" }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: { marginTop: '1px', maxHeight: 300, minWidth: "260px" }
          }
        }}
        MenuListProps={{ sx: { py: '0px !important' } }}
      >
        {options.map((option, index) => (
          <MenuItem
            key={option.value}
            selected={index === selectedIndex}
            onClick={(event) => handleMenuItemClick(event, index)}
            sx={{ px: '12px' }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      {options[selectedIndex].value === 'payg' && (
        <ButtonGroup
          variant="outlined"
          ref={teamButtonRef}
          aria-label="split team button"
          sx={{ boxShadow: 'none', height: '100%', width: '130px' }}
        >
          <Button
            sx={{
              width: "100px",
              minWidth: '100px',
              py: '6px',
              px: '12px'
            }}
            disabled={!teams?.length}
          >
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textTransform: 'none'
              }}
            >
              {selectedTeam?.name || "All Teams"}
            </Box>
          </Button>
          <Button
            size="small"
            onClick={handleTeamMenuOpen}
            sx={{ p: "0px !important", width: '10px' }}
            disabled={!teams?.length}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
      )}
      <Menu
        anchorEl={teamButtonRef.current}
        open={showTeamMenu}
        onClose={handleTeamMenuClose}
        slotProps={{
          paper: {
            style: { maxHeight: 300, minWidth: "100px" }
          }
        }}
      >
        <MenuItem onClick={() => handleTeamChange("")}>
          All Teams
        </MenuItem>
        {teams?.map((team: any, index: number) => (
          <MenuItem
            key={index}
            onClick={() => handleTeamChange(team)}
          >
            {team?.name}
          </MenuItem>
        ))}
      </Menu>
      <div>
        {msgConfig.snackFlag && (
          <SnackBarMessage snacBarValue={msgConfig} />
        )}
      </div>
    </Box>
  );
};

export default DownloadUserReport;
